import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { SiteLogo } from "../components/SiteLogo";
import api, { formatApiError } from "../lib/api";
import Starfield from "../components/landing/Starfield";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", remember: false });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/panel"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError("");
    if (!form.email.trim() || !form.password) {
      setError("E-posta ve şifre alanları zorunludur");
      return;
    }
    setLoading(true);
    try {
      const data = await login(form.email, form.password, form.remember);
      navigate(data.role === "admin" ? "/admin" : "/panel");
    } catch (err) {
      setError(formatApiError(err, "Giriş yapılamadı"));
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      toast.error("E-posta adresinizi giriniz");
      return;
    }
    try {
      const res = await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success(res.data.message);
      setForgotMode(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls =
    "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors";

  return (
    <div data-testid="login-page" className="relative min-h-screen bg-[#050505] flex items-center justify-center px-5 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0A0E17] to-[#050505]" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#0047FF]/10 blur-[130px]" />
      <Starfield count={50} />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex" data-testid="login-logo">
            <SiteLogo size="lg" />
          </Link>
          {forgotMode && (
            <>
              <h1 className="font-display font-700 text-xl text-white mt-7" data-testid="login-title">
                Şifremi Unuttum
              </h1>
              <p className="text-sm text-[#A0ABC0] mt-2">
                E-posta adresinizi girin, sıfırlama bağlantısı gönderelim.
              </p>
            </>
          )}
        </div>

        <div className="rounded-3xl border border-white/[0.08] bg-[#0A0E17]/90 backdrop-blur p-8">
          {forgotMode ? (
            <form onSubmit={submitForgot} data-testid="forgot-form">
              <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">E-posta</label>
              <input
                type="email"
                data-testid="forgot-email-input"
                className={inputCls}
                placeholder="ornek@mail.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
              />
              <button type="submit" data-testid="forgot-submit-button" className="btn-glow mt-6 w-full text-sm font-semibold text-white py-3.5 rounded-xl">
                Sıfırlama Bağlantısı Gönder
              </button>
              <button
                type="button"
                onClick={() => setForgotMode(false)}
                data-testid="forgot-back-button"
                className="mt-4 w-full text-center text-xs text-[#A0ABC0] hover:text-cyan-300 transition-colors"
              >
                Giriş ekranına dön
              </button>
            </form>
          ) : (
            <form onSubmit={submit} data-testid="login-form">
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">E-posta</label>
                  <input
                    type="email"
                    data-testid="login-email-input"
                    className={inputCls}
                    placeholder="ornek@mail.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#A0ABC0] mb-2">Şifre</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      data-testid="login-password-input"
                      className={`${inputCls} pr-11`}
                      placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="login-password-toggle"
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-cyan-300"
                      aria-label="Şifreyi göster"
                    >
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </div>
              </div>

              {error && (
                <p data-testid="login-error-message" className="mt-4 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                  {error}
                </p>
              )}

              <div className="mt-5 flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="login-remember-checkbox"
                    checked={form.remember}
                    onChange={(e) => setForm({ ...form, remember: e.target.checked })}
                    className="w-4 h-4 rounded border-white/20 bg-black/40 accent-cyan-400"
                  />
                  <span className="text-xs text-[#A0ABC0]">Beni Hatırla</span>
                </label>
                <button
                  type="button"
                  onClick={() => setForgotMode(true)}
                  data-testid="login-forgot-button"
                  className="text-xs text-cyan-300 hover:text-cyan-100 transition-colors"
                >
                  Şifremi Unuttum
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="btn-glow mt-7 w-full text-sm font-semibold text-white py-3.5 rounded-xl disabled:opacity-50"
              >
                {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/" data-testid="login-back-home" className="text-xs text-[#64748B] hover:text-cyan-300 transition-colors">
            ← Ana sayfaya dön
          </Link>
        </div>
      </div>
    </div>
  );
}
